import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

import ReportesApi from "api/reportesApi";
import TablaReporte from "./TablaReporte";
import ModalGrafico from "./ModalGrafico";
import { exportarAExcel, prepararDatosParaExportacion } from "../utils/exportarExcel";
import { exportarAPDF, formatters } from "../utils/exportarPDF";

/**
 * Componente de reporte de procesos por asesor
 * Muestra estadísticas y listado de procesos agrupados por asesor
 */
function ReportePorAsesor({ filtros = {} }) {
  const [datos, setDatos] = useState([]);
  const [totales, setTotales] = useState({});
  const [datosGrafico, setDatosGrafico] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalGraficoAbierto, setModalGraficoAbierto] = useState(false);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ReportesApi.obtenerReportePorAsesor(filtros);
      setDatos(response.data.datos || []);
      setTotales(response.data.totales || {});
      setDatosGrafico(response.data.grafico || {});
    } catch (error) {
      console.error("Error al cargar reporte por asesor:", error);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Definición de columnas de la tabla principal
  const columnas = [
    {
      header: "Asesor",
      field: "asesor_nombre",
      align: "left",
      width: "350px",
      noWrap: true,
    },
    {
      header: "Procesos",
      field: "total_procesos",
      align: "center",
      width: "10%",
      format: (valor) => formatters.numero(valor),
    },
    {
      header: "Borrador",
      field: "cant_borrador",
      align: "center",
      width: "8%",
    },
    {
      header: "En Proceso",
      field: "cant_en_proceso",
      align: "center",
      width: "10%",
    },
    {
      header: "Exitosos",
      field: "cant_exitosos",
      align: "center",
      width: "8%",
      render: (fila) => (
        <Chip label={fila.cant_exitosos} color="success" size="small" sx={{ fontWeight: "bold" }} />
      ),
    },
    {
      header: "Fallidos",
      field: "cant_fallidos",
      align: "center",
      width: "8%",
      render: (fila) => (
        <Chip label={fila.cant_fallidos} color="error" size="small" sx={{ fontWeight: "bold" }} />
      ),
    },
    {
      header: "Validados",
      field: "cant_validados",
      align: "center",
      width: "8%",
    },
    {
      header: "Admitidos",
      field: "cant_admitidos",
      align: "center",
      width: "8%",
    },
    // {
    //   header: "Días Promedio",
    //   field: "dias_promedio",
    //   align: "center",
    //   width: "10%",
    //   format: (valor) => `${valor} días`,
    // },
    {
      header: "% Éxito",
      field: "tasa_exito",
      align: "center",
      width: "10%",
      format: (valor) => formatters.porcentaje(valor),
    },
  ];

  // Renderizado de fila expandida (detalle de procesos)
  const renderFilaExpandida = (fila) => {
    if (!fila.procesos_detalle || fila.procesos_detalle.length === 0) {
      return (
        <SuiTypography variant="caption" textColor="text">
          No hay procesos para mostrar
        </SuiTypography>
      );
    }

    return (
      <>
        {/* Indicador de truncamiento */}
        {fila.total_procesos > 100 && (
          <SuiBox p={1.5} mb={1} bgcolor="warning.main" borderRadius="lg" sx={{ opacity: 0.9 }}>
            <SuiTypography variant="caption" fontWeight="medium" sx={{ color: "white" }}>
              ⚠️ Mostrando los 100 procesos más recientes de {fila.total_procesos} totales
            </SuiTypography>
          </SuiBox>
        )}

        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small">
            <TableBody>
              {/* Row de Headers */}
              <TableRow
                sx={{
                  backgroundColor: "#E8F5E9",
                  position: "sticky",
                  top: 0,
                  zIndex: 50,
                }}
              >
                <TableCell
                  sx={{
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: "bold",
                    fontSize: "0.813rem",
                  }}
                >
                  Identificador
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: "bold",
                    fontSize: "0.813rem",
                  }}
                >
                  Cliente
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: "bold",
                    fontSize: "0.813rem",
                  }}
                >
                  Fecha Creación
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: "bold",
                    fontSize: "0.813rem",
                  }}
                >
                  Estado
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: "bold",
                    fontSize: "0.813rem",
                  }}
                >
                  Días Gestión
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: "bold",
                    fontSize: "0.813rem",
                  }}
                >
                  Validado
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: "bold",
                    fontSize: "0.813rem",
                  }}
                >
                  Admitido
                </TableCell>
              </TableRow>

              {/* Filas de datos */}
              {fila.procesos_detalle.map((proceso, index) => (
                <TableRow key={index} hover>
                  <TableCell>{proceso.identificador}</TableCell>
                  <TableCell>{proceso.cliente}</TableCell>
                  <TableCell>{proceso.fecha_creacion}</TableCell>
                  <TableCell>
                    <Chip label={proceso.estado} size="small" />
                  </TableCell>
                  <TableCell align="center">{proceso.dias_gestion} días</TableCell>
                  <TableCell align="center">
                    {proceso.validado ? (
                      <Chip label="Sí" color="success" size="small" />
                    ) : (
                      <Chip label="No" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {proceso.admitido ? (
                      <Chip label="Sí" color="success" size="small" />
                    ) : (
                      <Chip label="No" color="default" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  // Exportar a Excel
  const handleExportarExcel = () => {
    const datosFormateados = prepararDatosParaExportacion(
      datos.map((item) => ({
        Asesor: item.asesor_nombre,
        "Total Procesos": item.total_procesos,
        Borrador: item.cant_borrador,
        "En Proceso": item.cant_en_proceso,
        Exitosos: item.cant_exitosos,
        Fallidos: item.cant_fallidos,
        Validados: item.cant_validados,
        Admitidos: item.cant_admitidos,
        "Días Promedio": item.dias_promedio,
        "Tasa de Éxito (%)": item.tasa_exito,
      })),
      {
        "Total Procesos": "numero",
        "Tasa de Éxito (%)": "numero",
      }
    );

    exportarAExcel(datosFormateados, "Reporte_Por_Asesor", "Asesores", {
      titulo: "Reporte de Procesos por Asesor",
      filtros: filtros,
    });
  };

  // Exportar a PDF
  const handleExportarPDF = () => {
    const columnasPDF = [
      { header: "Asesor", field: "asesor_nombre" },
      { header: "Total Procesos", field: "total_procesos", format: formatters.numero },
      { header: "Exitosos", field: "cant_exitosos" },
      { header: "Fallidos", field: "cant_fallidos" },
      { header: "Días Promedio", field: "dias_promedio", format: (v) => `${v} días` },
      { header: "Tasa Éxito", field: "tasa_exito", format: formatters.porcentaje },
    ];

    exportarAPDF({
      titulo: "Reporte de Procesos por Asesor",
      columnas: columnasPDF,
      datos: datos,
      filtros: filtros,
      totales: {
        "Total de Procesos": formatters.numero(totales.total_procesos || 0),
        "Procesos Exitosos": formatters.numero(totales.total_exitosos || 0),
        "Procesos Fallidos": formatters.numero(totales.total_fallidos || 0),
      },
    });
  };

  return (
    <Grid container spacing={2}>
      {/* Tarjetas de totales - 7 tarjetas en una línea */}
      <Grid item xs={6} sm={4} md={1.7}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #49a3f1 0%, #1A73E8 100%)",
            minHeight: "120px",
          }}
        >
          <SuiBox p={1.5} textAlign="center">
            <SuiTypography variant="h4" fontWeight="bold" sx={{ color: "white !important" }}>
              {formatters.numero(totales.total_procesos || 0)}
            </SuiTypography>
            <SuiTypography
              variant="caption"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              Total Procesos
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      <Grid item xs={6} sm={4} md={1.7}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #9E9E9E 0%, #616161 100%)",
            minHeight: "120px",
          }}
        >
          <SuiBox p={1.5} textAlign="center">
            <SuiTypography variant="h4" fontWeight="bold" sx={{ color: "white !important" }}>
              {formatters.numero(totales.total_borrador || 0)}
            </SuiTypography>
            <SuiTypography
              variant="caption"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              Borrador
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      <Grid item xs={6} sm={4} md={1.7}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #42A5F5 0%, #1976D2 100%)",
            minHeight: "120px",
          }}
        >
          <SuiBox p={1.5} textAlign="center">
            <SuiTypography variant="h4" fontWeight="bold" sx={{ color: "white !important" }}>
              {formatters.numero(totales.total_en_proceso || 0)}
            </SuiTypography>
            <SuiTypography
              variant="caption"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              En Proceso
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      <Grid item xs={6} sm={4} md={1.7}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #FFA726 0%, #FB8C00 100%)",
            minHeight: "120px",
          }}
        >
          <SuiBox p={1.5} textAlign="center">
            <SuiTypography variant="h4" fontWeight="bold" sx={{ color: "white !important" }}>
              {formatters.numero(totales.total_validados || 0)}
            </SuiTypography>
            <SuiTypography
              variant="caption"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              Validados
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      <Grid item xs={6} sm={4} md={1.7}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #AB47BC 0%, #8E24AA 100%)",
            minHeight: "120px",
          }}
        >
          <SuiBox p={1.5} textAlign="center">
            <SuiTypography variant="h4" fontWeight="bold" sx={{ color: "white !important" }}>
              {formatters.numero(totales.total_admitidos || 0)}
            </SuiTypography>
            <SuiTypography
              variant="caption"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              Admitidos
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      <Grid item xs={6} sm={4} md={1.7}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #66BB6A 0%, #43A047 100%)",
            minHeight: "120px",
          }}
        >
          <SuiBox p={1.5} textAlign="center">
            <SuiTypography variant="h4" fontWeight="bold" sx={{ color: "white !important" }}>
              {formatters.numero(totales.total_exitosos || 0)}
            </SuiTypography>
            <SuiTypography
              variant="caption"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              Exitosos
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      <Grid item xs={6} sm={4} md={1.7}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #EF5350 0%, #E53935 100%)",
            minHeight: "120px",
          }}
        >
          <SuiBox p={1.5} textAlign="center">
            <SuiTypography variant="h4" fontWeight="bold" sx={{ color: "white !important" }}>
              {formatters.numero(totales.total_fallidos || 0)}
            </SuiTypography>
            <SuiTypography
              variant="caption"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              Fallidos
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      {/* Tabla con datos */}
      <Grid item xs={12}>
        <TablaReporte
          titulo="Reporte Detallado por Asesor"
          columnas={columnas}
          datos={datos}
          loading={loading}
          expandible
          renderExpandido={renderFilaExpandida}
          onVerGrafico={() => setModalGraficoAbierto(true)}
          onExportarExcel={handleExportarExcel}
          onExportarPDF={handleExportarPDF}
          filasPorPaginaInicial={10}
        />
      </Grid>

      {/* Modal de gráfico */}
      <ModalGrafico
        abierto={modalGraficoAbierto}
        onCerrar={() => setModalGraficoAbierto(false)}
        titulo="Gráfico de Procesos por Asesor"
        tipoGrafico="bar"
        datosGrafico={datosGrafico}
      />
    </Grid>
  );
}

ReportePorAsesor.propTypes = {
  filtros: PropTypes.object,
};

export default ReportePorAsesor;
